import {lineBlueGrey, panelMetalGrey, panelWoodTan} from "../const";
import {itemGlazing, itemPanelMaterial, itemPanelPosition, MdFrameItem} from "../model/frame/MdFrameBlock";

// which of a flanking profile's rabbets a pane glazes into. Only a double-rabbet
// profile (STD) offers a choice — "profile" is the rest, which have one rabbet and
// so put the pane where the profile itself decides (CO mid-throat, SR flush).
export type GlazeSide = "outside" | "inside" | "profile";

// How a lite / fixed-panel cell is drawn, derived from its fill configuration:
//   - material → the fill colour
//   - panes    → a rabbet per pane; double glazing takes one on each side of the stop
//
// The elevation draws only the material — a cell's glazing / position reads off the
// section in the plan and frame profile views, so repeating it on the face there
// would only say the same thing twice.
export type CellFill = {
    material: "glass" | "metal" | "wood";
    panes: GlazeSide[];
};

export function cellFill(item: MdFrameItem): CellFill {
    if(item.kind === "fixedPanel") {
        const position = itemPanelPosition(item);
        return {
            material: itemPanelMaterial(item),
            panes: [position === "center" ? "profile" : position],
        };
    }

    switch(itemGlazing(item)) {
        case "outside": return {material: "glass", panes: ["outside"]};
        case "double": return {material: "glass", panes: ["outside", "inside"]};
        case "center": return {material: "glass", panes: ["profile"]};
        case "flushInside": return {material: "glass", panes: ["profile"]};
        case "flushOutside": return {material: "glass", panes: ["profile"]};
        default: return {material: "glass", panes: ["inside"]};
    }
}

// the face a flush-glazed lite sits on, or null for any other fill. Flush glazing
// has one rabbet (SR), so the profile itself must turn to put it on that face.
export function flushGlazedFace(item: MdFrameItem): "inside" | "outside" | null {
    if(item.kind !== "lite") return null;
    switch(itemGlazing(item)) {
        case "flushInside": return "inside";
        case "flushOutside": return "outside";
        default: return null;
    }
}

// a cell with no fill configuration of its own — the plan section's stand-in for a
// nested block.
export const plainPanelFill: CellFill = {material: "metal", panes: ["profile"]};

export function fillColor(material: CellFill["material"]): number {
    if(material === "glass") return lineBlueGrey;
    return material === "wood" ? panelWoodTan : panelMetalGrey;
}
